import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    ListGroup,
    Placeholder,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dashboardService from '@/shared/services/dashboardService';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { denverDayOf, formatAnswerDay } from '@/shared/utils/deadline';
import { formatDateTime } from '@/features/applications/student/CandidacyOfferCard';

const DAY_MS = 24 * 60 * 60 * 1000;

const KINDS = {
    offer: { icon: '✉️', kind: 'Offer', action: 'Review offer' },
    expired_offer: {
        icon: '⏰',
        kind: 'Expired offer',
        action: 'Choose next step',
    },
    interview: { icon: '🗓️', kind: 'Interview', action: 'View details' },
    revision: { icon: '↩️', kind: 'Returned', action: 'Edit and resubmit' },
    draft_request: { icon: '📝', kind: 'Draft', action: 'Continue' },
    draft_application: { icon: '📝', kind: 'Draft', action: 'Continue' },
    candidates_waiting: {
        icon: '👥',
        kind: 'Candidates',
        action: 'Review candidates',
    },
    onboarding: { icon: '✅', kind: 'Onboarding', action: 'See next steps' },
    contract: { icon: '📄', kind: 'Contract', action: null },
    no_candidates: { icon: '🔍', kind: 'Open places', action: 'View section' },
};
const FALLBACK_KIND = { icon: '📌', kind: 'To-do', action: 'Open' };

// Faculty see the same offer from the other side
const FACULTY_ACTIONS = {
    offer: 'View candidates',
    interview: 'View candidates',
};

const DUE_PREFIX = {
    offer: 'Answer by',
    expired_offer: 'Expired',
    draft_application: 'Closes',
};

const ROLE_LABELS = { student: 'As a student', faculty: 'As faculty' };

const AREAS = [
    { key: 'action', heading: 'Needs your action' },
    { key: 'upcoming', heading: 'Coming up' },
    { key: 'waiting', heading: 'Waiting on others' },
    { key: 'program', heading: 'Waiting for the program' },
];
const AREA_LIMIT = 3;

/** Whole Denver calendar days from `now` to `due`, negative when past. */
export function daysFrom(due, now = new Date()) {
    return Math.round(
        (Date.parse(denverDayOf(due)) - Date.parse(denverDayOf(now))) / DAY_MS
    );
}

function relativeDays(days) {
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days === -1) return 'yesterday';
    return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

/** "Answer by Oct 30, 2026, in 2 days"; null for an item with no date. */
export function dueText(item, now = new Date()) {
    if (!item.dueAt) return null;
    const relative = relativeDays(daysFrom(item.dueAt, now));
    if (item.type === 'interview') {
        return `${formatDateTime(item.dueAt)}, ${relative}`;
    }
    const prefix = DUE_PREFIX[item.type] || 'Due';
    return `${prefix} ${formatAnswerDay(item.dueAt)}, ${relative}`;
}

function actionLabel(item) {
    if (item.role === 'faculty' && FACULTY_ACTIONS[item.type]) {
        return FACULTY_ACTIONS[item.type];
    }
    return (KINDS[item.type] || FALLBACK_KIND).action;
}

/** "Friday, Nov 6, 11:59 PM Mountain time", the offer's answer-by moment. */
export function answerByText(value) {
    const moment = new Date(value);
    const day = moment.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Denver',
    });
    const time = moment
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: 'America/Denver',
        })
        .replace(/\s/g, ' ');
    return `${day}, ${time} Mountain time`;
}

function UrgentBadge() {
    return (
        <Badge
            bg="danger"
            text={badgeTextFor('danger')}
            className="me-2 text-nowrap"
        >
            Urgent
        </Badge>
    );
}

function TodoItem({ item, now, showRole }) {
    const { icon, kind } = KINDS[item.type] || FALLBACK_KIND;
    const due = dueText(item, now);
    const action = actionLabel(item);
    const title = item.section || item.title;
    return (
        <ListGroup.Item
            as="li"
            className="px-0 d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2"
        >
            <div className="d-flex gap-3">
                <span className="fs-4 lh-1" aria-hidden="true">
                    {icon}
                </span>
                <div>
                    {showRole && (
                        <div className="small text-body-secondary">
                            {ROLE_LABELS[item.role]}
                        </div>
                    )}
                    <div className="fw-semibold">
                        <span className="visually-hidden">{kind}: </span>
                        {title}
                    </div>
                    {item.meeting && <div>{item.meeting}</div>}
                    {item.detail && (
                        <div className="text-body-secondary">{item.detail}</div>
                    )}
                    {(due || item.urgent) && (
                        <div>
                            {item.urgent && <UrgentBadge />}
                            {due && <span>{due}</span>}
                        </div>
                    )}
                </div>
            </div>
            {item.area !== 'program' && (
                <Button
                    as={Link}
                    to={item.link}
                    variant="outline-primary"
                    className="todo-action"
                    aria-label={`${action}: ${title}`}
                >
                    {action}
                </Button>
            )}
        </ListGroup.Item>
    );
}

function TodoList({ items, now, showRole }) {
    return (
        <ListGroup as="ul" variant="flush">
            {items.map((item, index) => (
                <TodoItem
                    key={`${item.type}-${item.link}-${index}`}
                    item={item}
                    now={now}
                    showRole={showRole}
                />
            ))}
        </ListGroup>
    );
}

/** The first thing on a student's dashboard when an offer is waiting. */
function OffersPanel({ offers, now }) {
    const deadlines = offers
        .map((offer) => offer.dueAt)
        .filter(Boolean)
        .sort();
    const count = offers.length;
    return (
        <Card
            className="border-warning border-2 shadow-sm mb-4"
            data-testid="offers-panel"
        >
            <Card.Body>
                <h2 className="h5 mb-1">
                    {count === 1
                        ? 'You have an offer'
                        : `You have ${count} offers`}
                </h2>
                {deadlines.length > 0 && (
                    <p className="fw-semibold mb-2">
                        Answer by {answerByText(deadlines[0])}
                    </p>
                )}
                <TodoList items={offers} now={now} />
            </Card.Body>
        </Card>
    );
}

function AreaSection({ heading, items, now, showRole }) {
    const [expanded, setExpanded] = useState(false);
    const shown = expanded ? items : items.slice(0, AREA_LIMIT);
    return (
        <section aria-label={heading} className="mb-3">
            <h3 className="h6 mb-0">
                {heading}
                <Badge bg="secondary" className="ms-2 text-nowrap">
                    {items.length}
                    <span className="visually-hidden"> items</span>
                </Badge>
            </h3>
            <TodoList items={shown} now={now} showRole={showRole} />
            {items.length > AREA_LIMIT && (
                <Button
                    variant="link"
                    className="px-0"
                    aria-expanded={expanded}
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? 'Show fewer' : `View all ${items.length}`}
                    <span className="visually-hidden"> in {heading}</span>
                </Button>
            )}
        </section>
    );
}

function TodosSkeleton() {
    return (
        <div role="status" aria-busy="true">
            <span className="visually-hidden">Loading your to-dos</span>
            <Placeholder as="div" animation="glow" aria-hidden="true">
                {[0, 1, 2].map((row) => (
                    <div key={row} className="mb-3">
                        <Placeholder xs={7} className="d-block mb-2" />
                        <Placeholder xs={4} className="d-block" />
                    </div>
                ))}
            </Placeholder>
        </div>
    );
}

const isStudentOffer = (item) =>
    item.role === 'student' && item.type === 'offer';

/**
 * "Your to-dos": everything waiting on the signed-in student or faculty
 * member, in three areas (needs your action, coming up, waiting on others),
 * each with one link to where it is done. A student's open offers lead as
 * their own panel. The dashboard mounts it for students and faculty only.
 */
function TodosCard() {
    const [state, setState] = useState({ status: 'loading', items: [] });

    const load = useCallback(() => {
        setState({ status: 'loading', items: [] });
        return dashboardService
            .getTodos()
            .then((answer) =>
                setState({ status: 'ready', items: answer?.items || [] })
            )
            .catch(() => setState({ status: 'error', items: [] }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        dashboardService
            .getTodos()
            .then((answer) => {
                if (!cancelled) {
                    setState({ status: 'ready', items: answer?.items || [] });
                }
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error', items: [] });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const { status, items } = state;
    const now = new Date();
    const showRole = new Set(items.map((item) => item.role)).size > 1;
    const offers = items.filter(isStudentOffer);
    const rest = items.filter((item) => !isStudentOffer(item));

    let body;
    if (status === 'loading') {
        body = <TodosSkeleton />;
    } else if (status === 'error') {
        body = (
            <Alert variant="danger" className="mb-0">
                <p className="mb-2">Your to-dos couldn&apos;t be loaded.</p>
                <Button variant="outline-danger" size="sm" onClick={load}>
                    Retry
                </Button>
            </Alert>
        );
    } else if (items.length === 0) {
        body = <p className="mb-0">You&apos;re all caught up.</p>;
    } else {
        body = AREAS.map(({ key, heading }) => {
            const inArea = rest.filter(
                (item) => (item.area || 'action') === key
            );
            return inArea.length > 0 ? (
                <AreaSection
                    key={key}
                    heading={heading}
                    items={inArea}
                    now={now}
                    showRole={showRole}
                />
            ) : null;
        });
    }

    const showCard =
        status !== 'ready' || items.length === 0 || rest.length > 0;
    return (
        <>
            {status === 'ready' && offers.length > 0 && (
                <OffersPanel offers={offers} now={now} />
            )}
            {showCard && (
                <Card
                    className="border-0 shadow-sm mb-4"
                    data-testid="todos-card"
                >
                    <Card.Header className="bg-transparent border-0 d-flex align-items-center gap-2">
                        <h2 className="h5 mb-0">Your to-dos</h2>
                        {status === 'ready' && rest.length > 0 && (
                            <Badge bg="primary" className="text-nowrap">
                                {rest.length}
                                <span className="visually-hidden">
                                    {' '}
                                    {rest.length === 1 ? 'item' : 'items'}{' '}
                                    waiting
                                </span>
                            </Badge>
                        )}
                    </Card.Header>
                    <Card.Body>{body}</Card.Body>
                </Card>
            )}
        </>
    );
}

export default TodosCard;
